import { useRouter } from 'next/navigation';

import {
  type ButtonProps,
  Stack,
  type StackProps,
  toast,
} from '@backyard-ui/core';
import { useMediaQuery } from '@backyard-ui/hooks';
import { datadogRum } from '@datadog/browser-rum';
import * as tracker from '@leroy-merlin-br/backpack-analytics';

import { type CrossService } from '@/infra/services/catalog/crosses/get-crosses-services/model';
import { type Seller } from '@/infra/services/catalog/product/get-sellers/model';
import { type Response as AddProductsToCartResponse } from '@/infra/services/checkout/cart/add-products-to-cart';
import { algolia } from '@/infra/tracking/algolia';
import { pushAddToCartServicePDP } from '@/infra/tracking/events/add-to-cart-service-pdp';
import { sendToApp } from '@/infra/tracking/tracker/send-to-app';
import { getDataLayerItem } from '@/infra/tracking/utils';
import { type UseAddProductsToCartProps } from '@/presenters/hooks/checkout/cart/use-add-products-to-cart/use-add-products-to-cart';
import { useProductStore } from '@/presenters/store/catalog/product';

import { BaseButton } from './BaseButton';
import styles from './BuyButtons.styles';

interface BuyButtonsProps
  extends Pick<StackProps, 'direction'>,
    Pick<ButtonProps, 'size'>,
    Pick<UseAddProductsToCartProps, 'onError' | 'onSuccess' | 'postSuccess'> {
  seller?: Seller;
  quantity?: number;
  selectedService?: { id: number; isSelected: boolean } | null;
  serviceData?: CrossService;
  hasFloatingButton?: boolean;
}

export default function BuyButtons(props: Readonly<BuyButtonsProps>) {
  const {
    seller,
    direction,
    size,
    onError,
    onSuccess,
    quantity = 1,
    selectedService,
    serviceData,
    postSuccess,
    hasFloatingButton = false,
  } = props;

  const { product, category, currentSeller, variants } = useProductStore(
    (store) => store
  );

  const router = useRouter();

  const isNewBuyButtonsEnabled =
    process.env.NEXT_PUBLIC_FEATURE_FLIP_NEW_BUY_BUTTONS_ENABLED === 'true';

  const shouldShowFloatingButton =
    !useMediaQuery('sm') && hasFloatingButton && isNewBuyButtonsEnabled;

  const classNames = styles();

  const emitEvents = (response: AddProductsToCartResponse) => {
    try {
      const {
        data: { id: cartId, uniqueProductsCount },
      } = response;

      algolia.sendAddToCartEvent();

      sendToApp({
        type: 'updateCart',
        data: {
          cartId,
          uniqueProductsCount,
        },
      });

      if (selectedService?.isSelected && serviceData) {
        pushAddToCartServicePDP({
          product,
          currentSeller,
          variants,
          category,
          quantity,
          serviceData,
        });
      } else {
        tracker.pushAddToCart({
          pageType: 'product',
          item: getDataLayerItem({
            product,
            currentSeller,
            variants,
            category,
            quantity,
            boxQuantity: quantity,
          }),
        });
      }
    } catch (e) {
      datadogRum.addError(new Error('SendAddToCardEventException'), {
        product: {
          id: product._id,
        },
      });
    }
  };

  return (
    <>
      {shouldShowFloatingButton && (
        <div className={classNames.floatingButtonContainer()}>
          <BaseButton
            seller={seller}
            onError={onError}
            onSuccess={(data, variables, context) => {
              emitEvents(data);

              toast({
                description: 'Produto adicionado ao carrinho',
                appearance: 'success',
              });

              onSuccess?.(data, variables, context);
            }}
            size={size}
            quantity={quantity}
            dataGtmElement="Add To Cart Button"
            postSuccess={postSuccess}
            data-testid="add-to-cart-button-mobile"
          >
            Adicionar ao carrinho
          </BaseButton>
        </div>
      )}

      <Stack spacing="4" className="w-full" direction={direction}>
        {isNewBuyButtonsEnabled ? (
          <>
            <BaseButton
              variant="outline"
              seller={seller}
              onError={onError}
              onSuccess={(data, variables, context) => {
                emitEvents(data);

                router.push('/carrinho');

                toast({
                  description: 'Produto adicionado ao carrinho',
                  appearance: 'success',
                });

                onSuccess?.(data, variables, context);
              }}
              size={size}
              quantity={quantity}
              dataGtmElement="Buy Now Button"
              postSuccess={postSuccess}
              data-testid="buy-now-button"
            >
              Comprar agora
            </BaseButton>
            <BaseButton
              seller={seller}
              onError={onError}
              onSuccess={(data, variables, context) => {
                emitEvents(data);

                toast({
                  description: 'Produto adicionado ao carrinho',
                  appearance: 'success',
                });

                onSuccess?.(data, variables, context);
              }}
              size={size}
              quantity={quantity}
              dataGtmElement="Add To Cart Button"
              postSuccess={postSuccess}
              data-testid="add-to-cart-button"
            >
              Adicionar ao carrinho
            </BaseButton>
          </>
        ) : (
          <>
            <BaseButton
              variant="outline"
              seller={seller}
              onError={onError}
              onSuccess={(data, variables, context) => {
                emitEvents(data);

                toast({
                  description: 'Produto adicionado ao carrinho',
                  appearance: 'success',
                });

                onSuccess?.(data, variables, context);
              }}
              size={size}
              quantity={quantity}
              dataGtmElement="Add To Cart Button"
              postSuccess={postSuccess}
              data-testid="add-to-cart-button"
            >
              Adicionar ao carrinho
            </BaseButton>
            <BaseButton
              seller={seller}
              onError={onError}
              onSuccess={(data, variables, context) => {
                emitEvents(data);

                router.push('/carrinho');

                toast({
                  description: 'Produto adicionado ao carrinho',
                  appearance: 'success',
                });

                onSuccess?.(data, variables, context);
              }}
              size={size}
              quantity={quantity}
              dataGtmElement="Buy Now Button"
              postSuccess={postSuccess}
              data-testid="buy-now-button"
            >
              Comprar agora
            </BaseButton>
          </>
        )}
      </Stack>
    </>
  );
}
